import { useBusiness } from '@/contexts/BusinessContext'

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { StockTransactionModel } from '@/types'

import { formatDate, getStatusBadge, getTypeBadge } from './utils'
import { Spinner } from '@/components/ui/spinner'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  data: StockTransactionModel.StockTransaction | null
  loading: boolean
}

export const TransactionDetailDialog = ({ open, onOpenChange, data, loading }: Props) => {
  const { formatCurrency } = useBusiness()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Transaction details</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner className="size-5 text-muted-foreground" />
          </div>
        ) : !data ? null : (
          <div className="space-y-4">
            {/* Meta */}
            <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <div className="space-y-1">
                <p className="text-muted-foreground">Type</p>
                {getTypeBadge(data.type)}
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground">Status</p>
                {getStatusBadge(data.status)}
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground">Date</p>
                <p className="text-ink">{formatDate(data.created_at)}</p>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground">Description</p>
                <p className="text-ink">{data.description || '—'}</p>
              </div>
            </div>

            <Separator className="bg-border-subtle" />

            {/* Items */}
            <div>
              <p className="mb-3 text-sm font-semibold text-ink">
                Items ({data.items?.length ?? 0})
              </p>

              {!data.items || data.items.length === 0 ? (
                <p className="text-sm text-muted-foreground">No items found.</p>
              ) : (
                <div className="overflow-hidden rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Variation</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Cost</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead>Batch</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items.map((item) => (
                        <TableRow key={item._id}>
                          <TableCell className="font-medium">
                            {item.productVariation?.product?.name ?? '—'}
                          </TableCell>
                          <TableCell>{item.productVariation?.name ?? '—'}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {item.productVariation?.sku ?? '—'}
                          </TableCell>
                          <TableCell
                            className={`text-right font-medium tabular-nums ${item.quantity < 0 ? 'text-error-dark' : 'text-success-dark'}`}
                          >
                            {item.quantity > 0 ? '+' : ''}
                            {item.quantity}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatCurrency(item.purchase_price)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatCurrency(item.sale_price)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {item.batch_no || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
